import { useAuth } from '../auth/AuthContext'
import { WORDPRESS_LOGIN_URL } from '../auth/config'
import { Icon } from './Icon'

export const LAND_LOGIN_URL = WORDPRESS_LOGIN_URL

type AuthIdentityProps = {
  className?: string
  eyebrow?: string
  iconSize?: number
  ariaLabel?: string
  compact?: boolean
}

function classes(className: string | undefined, state: string) {
  return ['auth-identity', `auth-identity-${state}`, className]
    .filter(Boolean)
    .join(' ')
}

function identityCopy(eyebrow: string, label: string, action?: string, compact = false) {
  return (
    <span className="auth-identity-copy">
      {!compact && <small>{eyebrow}</small>}
      <strong>{label}</strong>
      {!compact && action && <em className="auth-identity-action">{action}</em>}
    </span>
  )
}

/**
 * Shared identity control for the Land portal, editor, mission/world header,
 * and Zone preview. Authentication never gates the surrounding experience.
 */
export function AuthIdentity({
  className,
  eyebrow = '현재 사용자',
  iconSize = 18,
  ariaLabel,
  compact = false,
}: AuthIdentityProps) {
  const {
    status,
    user,
    isAuthenticated,
    logout,
    refreshSession,
  } = useAuth()

  if (status === 'checking') {
    return (
      <div
        className={classes(className, 'checking')}
        role="status"
        aria-label={ariaLabel}
        aria-live="polite"
        aria-busy="true"
      >
        <Icon name="user" size={iconSize} />
        {identityCopy(eyebrow, '로그인 확인 중', undefined, compact)}
      </div>
    )
  }

  if (status === 'unavailable') {
    const retry = () => {
      void Promise.resolve(refreshSession()).catch(() => {
        // AuthContext owns the friendly unavailable state. Never surface raw
        // request details here because they may contain authentication data.
      })
    }

    return (
      <button
        className={classes(className, 'unavailable')}
        type="button"
        onClick={retry}
        aria-label={ariaLabel ?? '로그인 상태 다시 확인'}
      >
        <Icon name="user" size={iconSize} />
        {identityCopy(eyebrow, '연결 확인 필요', '다시 시도', compact)}
      </button>
    )
  }

  if (isAuthenticated) {
    const name = user?.name?.trim() || user?.email?.trim() || '회원'
    return (
      <button
        className={classes(className, 'authenticated')}
        type="button"
        onClick={logout}
        aria-label={ariaLabel ?? `${name} 로컬 로그아웃`}
      >
        <Icon name="user" size={iconSize} />
        {identityCopy(eyebrow, '회원', '로그아웃', compact)}
      </button>
    )
  }

  return (
    <a
      className={classes(className, 'guest')}
      href={LAND_LOGIN_URL}
      aria-label={ariaLabel ?? '핑퐁랜드 로그인'}
    >
      <Icon name="user" size={iconSize} />
      {identityCopy(eyebrow, '비회원', '로그인', compact)}
    </a>
  )
}
